import React, { PropsWithChildren } from 'react';
import clsx from 'clsx';
import { css, darken, styled } from '@mui/material/styles';
import TaskAlt from '@mui/icons-material/TaskAlt';
import Box from '@mui/material/Box';
import { Stack, Typography } from '@mui/material';
import { IconDocument } from '@maiia/design-system';
import sanitizeHtml from 'sanitize-html';
import dynamic from 'next/dynamic';
import { PatientInstructionDocumentDTO } from '@maiia/model/generated/model/api-patient/api-patient';
import {
  SANITIZE_HTML_DEFAULT_OPTIONS,
  sanitizeInnerHtml,
} from '@/components/utils/sanitize';
import { Instruction } from '@/components/utils/instruction';
import { Trans, useTranslation } from '@/src/i18n';
import useDocumentUrl from '@/src/hooks/useDocumentUrl';

const DocumentViewerDialogLite = dynamic(
  () =>
    import('@/components/organisms/DocumentViewer/DocumentViewerDialogLite'),
  { ssr: false },
);

export const INSTRUCTION_ALLOW_HTML_TAGS = [
  'br',
  'strong',
  'i',
  'p',
  'em',
  'span',
  'li',
  'ol',
  'ul',
  'del',
  'ins',
  'u',
  'a',
];

export const StyledInstructionItemHtml = styled(Box)(
  ({ theme: { spacing } }) => css`
    & > * {
      padding-block-start: 0.5em;
      padding-block-end: 0.5em;

      :first-child {
        padding-block-start: 0;
        margin-block-start: 0;
      }
      :last-child {
        padding-block-end: 0;
        margin-block-end: 0;
      }
    }

    ol,
    ul {
      padding-bottom: 0 !important; // reset inherited value
      margin-block-start: 1em;
      margin-block-end: 0;
      padding-left: ${spacing(3)}!important;
    }

    ul li {
      list-style-type: disc;
      li {
        list-style-type: circle;
        li {
          list-style-type: square;
        }
      }
    }

    ol li {
      list-style-type: decimal;
    }
  `,
);

export const IntructionItemHtml = ({ html }: { html: string }) => {
  return (
    <StyledInstructionItemHtml
      dangerouslySetInnerHTML={sanitizeInnerHtml(html, {
        allowedTags: INSTRUCTION_ALLOW_HTML_TAGS,
        allowedAttributes: {
          ...(typeof SANITIZE_HTML_DEFAULT_OPTIONS.allowedAttributes ===
            'object' && SANITIZE_HTML_DEFAULT_OPTIONS.allowedAttributes),
          a: ['href', 'target'],
        },
        transformTags: {
          a: sanitizeHtml.simpleTransform('a', { target: '_blank' }),
        },
      })}
    />
  );
};

const StyledInstructionItem = styled('li')(
  ({
    theme: {
      spacing,
      palette: { background, common, primary },
    },
  }) => css`
    /* Reset other declarations */
    white-space: normal;
    width: 100%;
    display: flex;
    align-items: center;
    background-color: ${background.secondary};
    border: 1px solid ${darken(background.secondary, 0.1)};
    padding: ${spacing(2)};
    border-radius: 4px;
    overflow-wrap: anywhere;

    &.accepted {
      border: 2px solid ${primary.main};
    }

    &.lite-design {
      background-color: unset;
      border: none;
      padding: ${spacing(0, 2, 0, 0)};
    }

    .MuiSvgIcon-root {
      fill: ${primary.main};
      margin-right: ${spacing(2)};
    }

    a {
      text-decoration: underline;
      color: ${primary.main};
    }

    .document-item {
      display: flex;
      align-items: center;
      background-color: ${common.white};
      border: 1px solid ${background.lightGrey};
      border-radius: ${spacing(1)};
      padding: ${spacing(1)} ${spacing(2)};
      cursor: pointer;

      .icon-container {
        background-color: ${background.lightGrey};
        width: ${spacing(4)};
        height: ${spacing(4)};
        border-radius: ${spacing(1)};
        display: flex;
        justify-content: center;
        align-items: center;
        margin-right: ${spacing(2)};
      }
    }

    .MuiTypography-subtitle1 {
      font-style: italic !important;
    }
  `,
);

const InstructionObject = ({ instruction }: { instruction: Instruction }) => {
  const { t } = useTranslation();
  const [
    selectedInstructionDocument,
    setSelectedInstructionDocument,
  ] = React.useState<PatientInstructionDocumentDTO>();

  const [openDocumentViewer, setOpenDocumentViewer] = React.useState<boolean>(
    false,
  );
  const countLink =
    instruction.instruction?.toLowerCase().match('</a>')?.length || 0;
  const countFile = instruction.attachments?.length || 0;
  const { data: documentUrl } = useDocumentUrl({
    documentId: selectedInstructionDocument?.id,
    instructionId: instruction?.id,
    showSnackbar: true,
  });

  const documentViewerFile = React.useMemo(() => {
    return documentUrl && selectedInstructionDocument
      ? {
          ...selectedInstructionDocument,
          url: documentUrl,
        }
      : undefined;
  }, [selectedInstructionDocument, documentUrl]);

  const handleClickDocument = async (
    document: PatientInstructionDocumentDTO,
  ) => {
    setSelectedInstructionDocument(document);
    setOpenDocumentViewer(true);
  };

  return (
    <>
      {instruction.shouldTranslate ? (
        <Trans
          i18nKey={instruction.instruction}
          parent={Box}
          components={{
            a: <a href={instruction.url} target="_blank" rel="noreferrer" />,
          }}
        />
      ) : (
        <IntructionItemHtml html={instruction.instruction || ''} />
      )}

      {instruction.attachments && (
        <Stack
          direction="column"
          spacing={1}
          sx={{ mt: 1, alignItems: 'flex-start' }}
        >
          {instruction.attachments.map(document => (
            <Box
              key={document.id}
              className="document-item"
              onClick={() => handleClickDocument(document)}
            >
              <Box className="icon-container">
                <IconDocument />
              </Box>
              <Typography>{document.name || t('download')}</Typography>
            </Box>
          ))}
        </Stack>
      )}

      {(countLink > 0 || countFile > 0) && (
        <Box mt={1}>
          {countLink > 0 && (
            <Typography variant="subtitle1">
              {t('instruction-with-link-notice', { count: countLink })}
            </Typography>
          )}
          {countFile > 0 && (
            <Typography variant="subtitle1">
              {t('instruction-with-file-notice', { count: countFile })}
            </Typography>
          )}
        </Box>
      )}

      {documentViewerFile && (
        <DocumentViewerDialogLite
          open={openDocumentViewer && !!documentViewerFile}
          handleClose={() => setOpenDocumentViewer(false)}
          document={documentViewerFile}
          fullScreen
        />
      )}
    </>
  );
};

type InstructionItemProps = PropsWithChildren<{
  instruction?: Instruction;
  liteDesign?: boolean;
  isAccepted?: boolean;
}>;

const InstructionItem = ({
  instruction,
  liteDesign,
  isAccepted,
  children,
}: InstructionItemProps) => {
  return (
    <StyledInstructionItem
      className={clsx(
        'instruction-item',
        isAccepted && 'accepted',
        liteDesign && 'lite-design',
      )}
    >
      {isAccepted && <TaskAlt />}
      <Box flex={1}>
        {instruction && <InstructionObject instruction={instruction} />}
        {children}
      </Box>
    </StyledInstructionItem>
  );
};

export default InstructionItem;
